/**
 * Drawer con la información de una historia completado: título + lista de intentos
 * (fecha, score y tiempo por intento). Local a MonthlyChallengesPage.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
} from '@/components/molecules/Drawer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { localizedField } from '@/utils/localizedField';
import { ROUTES, buildRoute } from '@/constants/routes';
import { usersAPI, type StoryHistoryItem, type StoryAttemptItem } from '@/services/api/users.api';
import styles from './CompletedStoryDrawer.module.scss';

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface CompletedStoryDrawerProps {
  item: StoryHistoryItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CompletedStoryDrawer({ item, open, onOpenChange }: CompletedStoryDrawerProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const side = isMobile ? 'bottom' : 'right';
  const dateLocale = i18n.language === 'en' ? 'en-US' : 'es-ES';

  const [attempts, setAttempts] = useState<StoryAttemptItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !item) {
      setAttempts([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    usersAPI
      .getStoryAttempts(item.storyId)
      .then((rows) => {
        if (!cancelled) setAttempts(rows);
      })
      .catch(() => {
        if (!cancelled) setAttempts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, item]);

  const formatDate = (ms: number) =>
    new Date(ms).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side={side} onSwipeToClose={() => onOpenChange(false)}>
        <DrawerHeader>
          <DrawerTitle>{item ? localizedField(item, 'storyTitle', i18n.language) : ''}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          {loading ? (
            <div className={styles.attemptsLoading}>
              <Spinner size="md" aria-label={t('common.loading')} />
            </div>
          ) : attempts.length === 0 ? (
            <p className={styles.attemptsEmpty}>{t('monthlyStoriesPage.noAttempts', 'Sin intentos registrados.')}</p>
          ) : (
            <ol className={styles.attempts} reversed>
              {attempts.map((a, idx) => {
                const number = attempts.length - idx;
                return (
                  <li key={a.id} className={styles.attempts__item}>
                    <div className={styles.attempts__header}>
                      <span className={styles.attempts__number}>
                        {t('monthlyStoriesPage.attemptNumber', { number, defaultValue: 'Intento {{number}}' })}
                      </span>
                      <span className={styles.attempts__date}>{formatDate(a.completedAt)}</span>
                    </div>
                    <div className={styles.attempts__metrics}>
                      <span>
                        <span className={styles.attempts__metricLabel}>{t('progressPage.tableScore')}</span>
                        <span className={styles.attempts__metricValue}>{a.score}%</span>
                      </span>
                      <span>
                        <span className={styles.attempts__metricLabel}>{t('progressPage.tableTime')}</span>
                        <span className={styles.attempts__metricValue}>{formatTime(a.timeSpent)}</span>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button
            variant="primary"
            fullWidth
            onClick={() => {
              if (!item) return;
              navigate(buildRoute(ROUTES.STUDENT.STORY, { id: item.storyId }));
            }}
          >
            {t('monthlyStoriesPage.reviewStory', 'Repasar historia')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default CompletedStoryDrawer;
